function trimText(value) {
  return String(value || "").trim();
}

function splitDisplayName(rawName) {
  const parts = trimText(rawName)
    .split(/\s+/)
    .filter(Boolean);

  if (parts.length === 0) {
    return { firstName: "", lastName: "" };
  }

  if (parts.length === 1) {
    return { firstName: parts[0], lastName: "" };
  }

  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(" "),
  };
}

function formatMatchDate(value) {
  const raw = trimText(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    return raw.slice(0, 10);
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return raw;
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatTotalScore(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric.toFixed(2) : "0.00";
}

function normalizeLevel(value) {
  const text = trimText(value).toLowerCase();
  if (text.includes("level 2") || text.includes("level ii")) {
    return "Level II";
  }
  if (text.includes("level 1") || text.includes("level i")) {
    return "Level I";
  }
  if (text.includes("2 gun") || text.includes("two gun")) {
    return "2 Gun";
  }
  return trimText(value);
}

function normalizeType(value) {
  const text = trimText(value).toLowerCase();
  if (text.startsWith("club")) {
    return "Club";
  }
  if (text.startsWith("state")) {
    return "State";
  }
  if (text.startsWith("regional")) {
    return "Regional";
  }
  if (text.startsWith("national")) {
    return "National";
  }
  return trimText(value);
}

function sanitizeFileName(rawName) {
  const trimmed = trimText(rawName) || "ARC Match";
  return `${trimmed.replaceAll("/", "-").replaceAll(":", " -")}_report.json`;
}

export function buildNraArcReport({ match, leaderboardRows = [], registrations = [] }) {
  const registrationsByIdentity = new Map();
  for (const registration of Array.isArray(registrations) ? registrations : []) {
    [
      registration?.participantId,
      registration?.primaryEntryId,
      registration?.entryId,
      ...(Array.isArray(registration?.entryIds) ? registration.entryIds : []),
      registration?.registrationId,
      registration?.competitorId,
      registration?.legacyCompetitorId,
    ].forEach((identity) => {
      const key = trimText(identity);
      if (key) {
        registrationsByIdentity.set(key, registration);
      }
    });
  }

  const shooters = (Array.isArray(leaderboardRows) ? leaderboardRows : [])
    .filter((row) => trimText(row?.competitorName))
    .map((row) => {
      const identities = [
        row?.participantId,
        row?.canonicalEntryId,
        row?.entryId,
        row?.registrationId,
        row?.competitorId,
        row?.legacyCompetitorId,
      ].map(trimText).filter(Boolean);
      const registration = identities.map((identity) => registrationsByIdentity.get(identity)).find(Boolean) || null;
      const name = splitDisplayName(
        registration?.displayName || row?.competitorName || "",
      );
      const division = trimText(registration?.divisionId || row?.divisionId);
      const email = trimText(registration?.email);
      const classification = trimText(
        registration?.classification || registration?.class || "",
      );
      const category = Array.isArray(registration?.categories)
        ? registration.categories.filter((value) => trimText(value))
        : [];
      const nraCompetitiveNumber = trimText(
        registration?.nraCompetitiveShootingNumber ||
          registration?.nraCompetitiveMemberNumber ||
          registration?.nraMemberId ||
          "",
      );
      const nraPrimaryNumber = trimText(
        registration?.nraPrimaryNumber ||
          registration?.nraPrimaryMemberNumber ||
          registration?.nraPrimaryMemberId ||
          "",
      );

      const shooter = {
        "First Name": name.firstName,
        "Last Name": name.lastName,
        Email: email,
        Division: division,
        Class: classification,
        Category: category,
        "Member Number: (NRA Competitive Shooting)": nraCompetitiveNumber,
        "Total Score": formatTotalScore(row?.totalScore),
      };

      if (nraPrimaryNumber) {
        shooter["Member Number: (NRA Primary)"] = nraPrimaryNumber;
      }

      return shooter;
    });

  const payload = {
    "Match Name": trimText(match?.matchName || match?.name),
    "Match Date": formatMatchDate(match?.startDate || match?.matchDate),
    Level: normalizeLevel(match?.level),
    Type: normalizeType(match?.sanctionedType),
    Shooters: shooters,
  };

  const stats = {
    shooterCount: shooters.length,
    missingEmailCount: shooters.filter((item) => !trimText(item.Email)).length,
    missingDivisionCount: shooters.filter((item) => !trimText(item.Division)).length,
    missingClassificationCount: shooters.filter((item) => !trimText(item.Class)).length,
    missingCompetitiveMemberCount: shooters.filter(
      (item) =>
        !trimText(item["Member Number: (NRA Competitive Shooting)"]),
    ).length,
  };

  return {
    payload,
    fileName: sanitizeFileName(match?.matchName || match?.name),
    stats,
  };
}

export function exportNraArcReportFile(report) {
  const blob = new Blob([JSON.stringify(report.payload, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = report.fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function formatNraArcExportSummary(stats) {
  const safeStats = stats || {};
  const notes = [];

  if (safeStats.missingEmailCount > 0) {
    notes.push(`${safeStats.missingEmailCount} missing email`);
  }
  if (safeStats.missingDivisionCount > 0) {
    notes.push(`${safeStats.missingDivisionCount} missing division`);
  }
  if (safeStats.missingClassificationCount > 0) {
    notes.push(`${safeStats.missingClassificationCount} missing class`);
  }
  if (safeStats.missingCompetitiveMemberCount > 0) {
    notes.push(
      `${safeStats.missingCompetitiveMemberCount} missing NRA competitive number`,
    );
  }

  if (notes.length === 0) {
    return `Exported ${safeStats.shooterCount || 0} shooters in NRA JSON format.`;
  }

  return `Exported ${safeStats.shooterCount || 0} shooters. Left blank: ${notes.join(", ")}.`;
}
